import { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function AlertSummaryTable({ entriesPerPage, canSearch, showTotalEntries, table, pagination, isSorted, noEndBorder }) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries.map((el) => el.toString()) : ["5", "10", "15", "20", "25"];

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  useEffect(() => setPageSize(defaultValue), [defaultValue, setPageSize]);

  const [search, setSearch] = useState(globalFilter);
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = (column) => {
    if (isSorted && column.isSorted) return column.isSortedDesc ? "desc" : "asce";
    return isSorted ? "none" : false;
  };

  return (
    <MDBox sx={{ width: "100%" }}>
      <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} p={3} gap={2}>
        {entriesPerPage && (
          <MDBox display="flex" alignItems="center">
            <Autocomplete
              disableClearable
              value={pageSize.toString()}
              options={entries}
              onChange={(e, v) => setPageSize(parseInt(v, 10))}
              size="small"
              sx={{ width: "5rem" }}
              renderInput={(params) => <MDInput {...params} />}
            />
            <MDTypography variant="caption" color="secondary" ml={1}>entries per page</MDTypography>
          </MDBox>
        )}
        {canSearch && (
          <MDBox width={{ xs: "100%", sm: "12rem" }}>
            <MDInput
              placeholder="Search Vehicle ID..."
              value={search || ""}
              size="small"
              fullWidth
              onChange={({ currentTarget }) => {
                setSearch(currentTarget.value);
                onSearchChange(currentTarget.value);
              }}
            />
          </MDBox>
        )}
      </MDBox>

      <TableContainer sx={{ boxShadow: "none", overflowX: "auto" }}>
        <Table {...getTableProps()}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, idx) => (
              <TableRow key={idx} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <DataTableHeadCell
                    key={index}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    align={column.align ? column.align : "left"}
                    sorted={setSortedValue(column)}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, idx) => {
              prepareRow(row);
              return (
                <TableRow key={idx} {...row.getRowProps()}>
                  {row.cells.map((cell, index) => (
                    <DataTableBodyCell
                      key={index}
                      noBorder={noEndBorder && rows.length - 1 === idx}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <MDBox display="flex" flexDirection={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" p={3} gap={3}>
        {showTotalEntries && (
          <MDTypography variant="button" color="secondary">
            Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, rows.length)} of {rows.length} entries
          </MDTypography>
        )}

        {pageOptions.length > 1 && (
          <MDPagination variant={pagination.variant || "gradient"} color={pagination.color || "info"} sx={{ flexWrap: "wrap", justifyContent: "center" }}>
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {/* Displaying only 5 pages at a time or input if too many */}
            {pageOptions.length > 6 ? (
              <MDBox display="flex" alignItems="center" px={1}>
                <MDInput
                  type="number"
                  size="small"
                  value={pageIndex + 1}
                  onChange={(e) => gotoPage(Number(e.target.value) - 1)}
                  sx={{ width: "60px", mx: 1 }}
                />
                <MDTypography variant="caption">of {pageOptions.length}</MDTypography>
              </MDBox>
            ) : (
              pageOptions.map((opt) => (
                <MDPagination item key={opt} onClick={() => gotoPage(opt)} active={pageIndex === opt}>{opt + 1}</MDPagination>
              ))
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </MDBox>
  );
}

AlertSummaryTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: true,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

AlertSummaryTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([PropTypes.shape({ defaultValue: PropTypes.number, entries: PropTypes.arrayOf(PropTypes.number) }), PropTypes.bool]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({ variant: PropTypes.oneOf(["contained", "gradient"]), color: PropTypes.string }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default AlertSummaryTable;